import { useState } from "react";
import api from "../api";
import {
  Field,
  FileInput,
  TextInput,
  Segmented,
  Actions,
  PrimaryButton,
  ResultBanner,
  BusyNote,
  ProgressBar,
  CardIntro,
} from "./ui/Form";

/**
 * Geocode a CSV of addresses in one pass.
 *
 * Replaces the old two-card flow, which geocoded every row twice: the first
 * step kept only the matched address string, and the second looked the same
 * rows up again purely to read coordinates off the response. Both come from one
 * lookup now.
 *
 * The address can arrive either as separate street/city columns or as a single
 * column that already holds a full address -- the latter had no path through
 * the old flow.
 */

type Mode = "separate" | "single";

const REMEMBER_KEY = "pioneer.geocode.columns";

function remembered(): { address: string; city: string; single: string; mode: Mode } {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    if (raw) return { mode: "separate", address: "", city: "", single: "", ...JSON.parse(raw) };
  } catch {
    /* unusable storage is not worth failing over */
  }
  return { mode: "separate", address: "", city: "", single: "" };
}

export default function GeocodeCard() {
  const saved = remembered();

  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<Mode>(saved.mode);
  const [addressCol, setAddressCol] = useState(saved.address);
  const [cityCol, setCityCol] = useState(saved.city);
  const [singleCol, setSingleCol] = useState(saved.single);
  const [withCoords, setWithCoords] = useState(true);

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);

  const columnsReady =
    mode === "single" ? singleCol.trim().length > 0 : Boolean(addressCol.trim() && cityCol.trim());
  const ready = Boolean(file) && columnsReady;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;

    setBusy(true);
    setResult(null);

    const form = new FormData();
    form.append("csv", file as File);
    form.append("mode", mode);
    form.append("with_coords", String(withCoords));
    if (mode === "single") {
      form.append("single_col", singleCol.trim());
    } else {
      form.append("address_col", addressCol.trim());
      form.append("city_col", cityCol.trim());
    }

    try {
      const res = await api.post("/geocode-addresses", form, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "geocoded.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      try {
        localStorage.setItem(
          REMEMBER_KEY,
          JSON.stringify({
            mode,
            address: addressCol.trim(),
            city: cityCol.trim(),
            single: singleCol.trim(),
          })
        );
      } catch {
        /* non-fatal */
      }

      const rows = Number(res.headers["x-geocode-rows"] ?? 0);
      const matched = Number(res.headers["x-geocode-matched"] ?? 0);
      const unmatched = Number(res.headers["x-geocode-unmatched"] ?? 0);
      setResult({
        // Some rows failing to match is normal, but it should not read as an
        // unqualified success -- those rows need looking at by hand.
        status: unmatched > 0 ? "failure" : "success",
        message:
          unmatched > 0
            ? `Matched ${matched} of ${rows}. ${unmatched} row${unmatched === 1 ? "" : "s"} could not be matched — check them in the downloaded file.`
            : `Matched all ${rows} rows. Download started.`,
        details:
          unmatched > 0
            ? "Unmatched rows show 'Not Found' in Geocoded_Address and blank coordinates."
            : undefined,
      });
    } catch (err: any) {
      let message = err.message;
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text()).message ?? message;
        } catch {
          /* keep the original */
        }
      }
      setResult({ status: "failure", message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <CardIntro>
        Looks up each address and adds a standardised{" "}
        <code className="font-mono bg-paper-2 px-1 rounded">Geocoded_Address</code> column,
        plus <code className="font-mono bg-paper-2 px-1 rounded">X</code>/
        <code className="font-mono bg-paper-2 px-1 rounded">Y</code> coordinates if you want them.
      </CardIntro>

      <Field label="Address CSV">
        <FileInput accept=".csv" file={file} onFile={setFile} disabled={busy} />
      </Field>

      <div>
        <div className="eyebrow mb-1.5">How is the address stored?</div>
        <Segmented<Mode>
          value={mode}
          onChange={setMode}
          disabled={busy}
          options={[
            ["separate", "Separate columns"],
            ["single", "One address column"],
          ]}
        />
      </div>

      {mode === "separate" ? (
        <div className="grid grid-cols-1 min-[560px]:grid-cols-2 gap-3 animate-[fadeIn_.2s_ease-out_both]">
          <Field label="Street column" hint="Header exactly as it appears in the CSV">
            <TextInput
              value={addressCol}
              onChange={(e) => setAddressCol(e.target.value)}
              placeholder="e.g. Address"
              disabled={busy}
            />
          </Field>
          <Field label="City column" hint="City, and ZIP if it is in the same column">
            <TextInput
              value={cityCol}
              onChange={(e) => setCityCol(e.target.value)}
              placeholder="e.g. City"
              disabled={busy}
            />
          </Field>
        </div>
      ) : (
        <div className="animate-[fadeIn_.2s_ease-out_both]">
          <Field
            label="Address column"
            hint="A column already holding the whole address, e.g. '4641 E Princeton Ave, Fresno, CA 93703'"
          >
            <TextInput
              value={singleCol}
              onChange={(e) => setSingleCol(e.target.value)}
              placeholder="e.g. Full_Address"
              disabled={busy}
            />
          </Field>
        </div>
      )}

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={withCoords}
          onChange={(e) => setWithCoords(e.target.checked)}
          disabled={busy}
          className="mt-0.5 accent-brand"
        />
        <span className="text-sm">
          Also add X/Y coordinates
          <span className="block text-ink-faint text-[12px]">
            Free — they come from the same lookup. Untick only if you just want addresses validated.
          </span>
        </span>
      </label>

      <Actions>
        <PrimaryButton type="submit" busy={busy} busyLabel="Geocoding…" disabled={!ready}>
          Geocode addresses
        </PrimaryButton>
      </Actions>

      {busy && <ProgressBar />}
      {busy && (
        <BusyNote>
          Looking up one address at a time. A few thousand rows can take several minutes.
        </BusyNote>
      )}

      <ResultBanner result={result} />
    </form>
  );
}
